import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./BetsPage.css";
import "flag-icons/css/flag-icons.min.css";


import {
  Chart,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";

Chart.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Tooltip
);

const API = process.env.REACT_APP_API_URL || "/api";

const LEAGUE_FLAG_CODES = {
  EPL: "gb-eng",
  La_liga: "es",
  Serie_A: "it",
  Bundesliga: "de",
  Ligue_1: "fr",
};

function Flag({ league, size = "sm" }) {
  const code = LEAGUE_FLAG_CODES[league];
  if (!code) return null;
  const style =
    size === "lg"
      ? { width: "1.4em", height: "1.05em", borderRadius: "2px", flexShrink: 0 }
      : {
          width: "1.1em",
          height: "0.825em",
          borderRadius: "2px",
          flexShrink: 0,
        };
  return <span className={`fi fi-${code}`} style={style} title={league} />;
}

const LEAGUE_COLORS = {
  EPL: "#38003c",
  La_liga: "#ef2340",
  Serie_A: "#003da5",
  Bundesliga: "#d20515",
  Ligue_1: "#091c3e",
};

const OUTCOME_COLORS = { H: "#22c55e", D: "#f59e0b", A: "#3b82f6" };

// Casas accesibles desde Francia
const FRANCE_OK_BM = new Set([
  "winamax.fr",
  "winamax",
  "bet365",
  "bwin",
  "betclic",
  "betclic.fr",
  "unibet.fr",
  "zebet",
  "vbet",
  "vbet.fr",
  "netbet",
  "netbet.fr",
  "pmu",
  "pmu.fr",
  "parions-sport",
  "parionssport",
  "france.pari",
]);
function isFranceOk(bm) {
  const lc = bm.toLowerCase();
  return (
    FRANCE_OK_BM.has(lc) ||
    [
      "winamax",
      "betclic",
      "unibet.fr",
      "zebet",
      "vbet",
      "netbet",
      "parions",
    ].some((k) => lc.includes(k))
  );
}

function edgeColor(edge) {
  if (edge >= 15) return "#22c55e";
  if (edge >= 10) return "#84cc16";
  if (edge >= 5) return "#f59e0b";
  return "#94a3b8";
}

function confColor(conf) {
  if (conf >= 0.7) return "#f59e0b";
  if (conf >= 0.6) return "#3b82f6";
  return "#64748b";
}

function formatDate(dateStr, locale) {
  const d = new Date(dateStr);
  return d.toLocaleDateString(locale || undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function plColor(pl) {
  return pl >= 0 ? "#22c55e" : "#ef4444";
}

function groupByDay(picks) {
  const groups = {};
  picks.forEach((p) => {
    const day = p.date.slice(0, 10);
    if (!groups[day]) groups[day] = [];
    groups[day].push(p);
  });
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
}

// ── sub-components ────────────────────────────────────────────

function StatCard({ label, value, sub, color, big }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div
        className="stat-value"
        style={{
          color: color || "var(--text-primary)",
          fontSize: big ? "2rem" : "1.5rem",
        }}
      >
        {value}
      </div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function BankrollChart({ weekly, bets, onBetClick }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!bets || bets.length === 0) return;

    // Destruir instancia anterior si existe
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Construir puntos: inicio + una entrada por apuesta
    let bankroll = 1000;
    const labels = [t("bets.chart_start")];
    const bankrollData = [1000];
    const pointColors = ["#888780"];
    const pointRadii = [4];

    bets.forEach((b, i) => {
      bankroll = parseFloat((bankroll + b.pl_flat).toFixed(2));
      labels.push(`#${i + 1}`);
      bankrollData.push(bankroll);
      pointColors.push(b.won ? "#22c55e" : "#ef4444");
      pointRadii.push(6);
    });

    const ctx = canvasRef.current.getContext("2d");
    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Bankroll",
            data: bankrollData,
            borderColor: "#3b82f6",
            borderWidth: 2,
            pointBackgroundColor: pointColors,
            pointRadius: pointRadii,
            pointHoverRadius: 8,
            tension: 0.3,
            fill: false,
          },
          {
            label: "Breakeven",
            data: Array(bankrollData.length).fill(1000),
            borderColor: "#475569",
            borderWidth: 1,
            borderDash: [6, 4],
            pointRadius: 0,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.datasetIndex === 1) return "Breakeven: €1 000";
                const i = ctx.dataIndex;
                if (i === 0) return "Inicio: €1 000";
                const b = bets[i - 1];
                const sign = b.pl_flat >= 0 ? "+" : "";
                const desc = b._isParlay
                  ? `${b.home_team}: ${b.away_team}`
                  : `${b.home_team} — ${b.away_team}`;
                return [
                  `Bankroll: €${ctx.parsed.y.toFixed(2)}`,
                  `P/L: ${sign}€${Math.abs(b.pl_flat).toFixed(2)}`,
                  desc,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            ticks: { font: { size: 11 }, color: "#64748b", maxRotation: 0 },
            grid: { color: "rgba(100,116,139,0.15)" },
          },
          y: {
            min: Math.min(...bankrollData) - 30,
            max: Math.max(...bankrollData, 1000) + 30,
            ticks: {
              font: { size: 11 },
              color: "#64748b",
              callback: (v) => "€" + v,
            },
            grid: { color: "rgba(100,116,139,0.15)" },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [bets, t]);

  if (!bets || bets.length === 0) {
    return <div className="chart-empty">{t("bets.chart_no_data")}</div>;
  }

  const last = bets[bets.length - 1];
  const finalBk = bets.reduce((acc, b) => acc + b.pl_flat, 1000);
  const bkColor = finalBk >= 1000 ? "#22c55e" : "#ef4444";

  return (
    <div className="bankroll-chart-new">
      {/* Leyenda */}
      <div className="bkc-legend">
        <span className="bkc-leg-item">
          <span className="bkc-dot" style={{ background: "#3b82f6" }} />
          Bankroll (€)
        </span>
        <span className="bkc-leg-item">
          <span className="bkc-dot" style={{ background: "#22c55e" }} />
          {t("bets.chart_won")}
        </span>
        <span className="bkc-leg-item">
          <span className="bkc-dot" style={{ background: "#ef4444" }} />
          {t("bets.chart_lost")}
        </span>
        <span className="bkc-leg-item bkc-leg-sep">
          <span className="bkc-dot bkc-dot-dash" />
          Breakeven €1 000
        </span>
        <span className="bkc-leg-final" style={{ color: bkColor }}>
          €{finalBk.toFixed(2)}
        </span>
      </div>

      {/* Canvas */}
      <div style={{ position: "relative", width: "100%", height: "260px" }}>
        <canvas ref={canvasRef} />
      </div>

      {/* Tabla detalle */}
      <div className="bkc-table-wrap">
        <table className="bkc-table">
          <thead>
            <tr>
              <th>{t("bets.hist_col_date")}</th>
              <th>{t("bets.hist_col_match")}</th>
              <th style={{ textAlign: "right" }}>{t("bets.hist_col_odds")}</th>
              <th style={{ textAlign: "right" }}>P/L</th>
              <th style={{ textAlign: "right" }}>Bankroll</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let running = 1000;
              return bets.map((b, i) => {
                running = parseFloat((running + b.pl_flat).toFixed(2));
                return (
                  <tr
                    key={i}
                    className={b.won ? "bkc-won" : "bkc-lost"}
                    onClick={() => !b._isParlay && onBetClick && onBetClick(b)}
                    style={{ cursor: (!b._isParlay && onBetClick) ? "pointer" : "default" }}
                  >
                    <td className="bkc-date">{formatDate(b.date)}</td>
                    <td className="bkc-match">
                      <span
                        style={{
                          color: b.won ? "#22c55e" : "#ef4444",
                          marginRight: 6,
                        }}
                      >
                        {b.won ? "✓" : "✗"}
                      </span>
                      {b._isParlay
                        ? <>{b.home_team}: <span style={{ color: "#94a3b8" }}>{b.away_team}</span></>
                        : <>{b.home_team} — {b.away_team}</>
                      }
                    </td>
                    <td style={{ textAlign: "right" }}>{b.odds ? `x${b.odds.toFixed(2)}` : "—"}</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: b.pl_flat >= 0 ? "#22c55e" : "#ef4444",
                        fontWeight: 600,
                      }}
                    >
                      {b.pl_flat >= 0 ? "+" : ""}€
                      {Math.abs(b.pl_flat).toFixed(2)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                      €{running.toFixed(2)}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StrategyBanner() {
  const { t } = useTranslation();
  return (
    <div className="strategy-banner">
      <div className="sb-title">{t("bets.strategy_title")}</div>
      <div className="sb-strategies">
        <div className="sb-item">
          <span
            className="sb-badge"
            style={{
              background: "#22c55e22",
              color: "#22c55e",
              border: "1px solid #22c55e44",
            }}
          >
            {t("bets.strategy_value10_badge")}
          </span>
          <span className="sb-desc">{t("bets.strategy_value10_desc")}</span>
        </div>
        <div className="sb-item">
          <span
            className="sb-badge"
            style={{
              background: "#f59e0b22",
              color: "#f59e0b",
              border: "1px solid #f59e0b44",
            }}
          >
            {t("bets.strategy_liga_badge")}
          </span>
          <span className="sb-desc">{t("bets.strategy_liga_desc")}</span>
        </div>
        <div className="sb-item">
          <span
            className="sb-badge"
            style={{
              background: "#8b5cf622",
              color: "#a78bfa",
              border: "1px solid #8b5cf644",
            }}
          >
            {t("bets.strategy_double_badge")}
          </span>
          <span className="sb-desc">{t("bets.strategy_double_desc")}</span>
        </div>
        <div className="sb-item">
          <span
            className="sb-badge"
            style={{
              background: "#f9731622",
              color: "#fb923c",
              border: "1px solid #f9731644",
            }}
          >
            {t("bets.strategy_triple_badge")}
          </span>
          <span className="sb-desc">{t("bets.strategy_triple_desc")}</span>
        </div>
        <div className="sb-item sb-avoid">
          <span
            className="sb-badge"
            style={{
              background: "#ef444422",
              color: "#ef4444",
              border: "1px solid #ef444444",
            }}
          >
            {t("bets.strategy_avoid_badge")}
          </span>
          <span className="sb-desc">{t("bets.strategy_avoid_desc")}</span>
        </div>
      </div>
    </div>
  );
}

function ParlayCard({ parlay, franceOnly, type = "double", index = null }) {
  const { t, i18n } = useTranslation();
  if (!parlay) return null;
  const { legs, combined_odds, combined_prob } = parlay;
  const isTriple = type === "triple";
  const probPct = (combined_prob * 100).toFixed(1);
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };

  return (
    <div className={`parlay-card${isTriple ? " parlay-triple" : ""}`}>
      <div className="parlay-header">
        <span className="parlay-title">
          {isTriple ? t("bets.triple_label") : t("bets.double_label")}
          {index !== null && (
            <span className="parlay-index">
              {" "}
              — {t("bets.option_n", { n: index })}
            </span>
          )}
        </span>
        <span className="parlay-hint">
          {isTriple ? t("bets.triple_hint") : t("bets.double_hint")}
        </span>
        {parlay.bookmaker ? (
          <span className="pl-bm parlay-bm">{parlay.bookmaker}</span>
        ) : (
          <span className="parlay-no-bm">{t("bets.no_bm")}</span>
        )}
      </div>
      <div className="parlay-legs">
        {legs.map((leg, i) => (
          <div key={i} className="parlay-leg">
            <Flag league={leg.league} />
            <span className="pl-match">
              {leg.home_team} vs {leg.away_team}
            </span>
            <span
              className="pl-pred"
              style={{ color: OUTCOME_COLORS[leg.predicted_result] }}
            >
              {OUTCOME_LABELS[leg.predicted_result]}
            </span>
            {leg.odds > 0 && (
              <span className="pl-odds">@{leg.odds.toFixed(2)}</span>
            )}
            {leg.best_edge > 0 && (
              <span
                className="pl-edge"
                style={{ color: edgeColor(leg.best_edge) }}
              >
                +{leg.best_edge.toFixed(1)}%
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="parlay-footer">
        {combined_odds > 0 && (
          <span className="parlay-combined">
            {t("bets.combined_odds")}:{" "}
            <strong>{combined_odds.toFixed(2)}</strong>
          </span>
        )}
        <span className="parlay-prob">
          {t("bets.model_prob")}: <strong>{probPct}%</strong>
        </span>
      </div>
    </div>
  );
}

function ProbBar({ probHome, probDraw, probAway, predicted }) {
  return (
    <div className="prob-bar-wrap">
      <div className="prob-bar">
        <div
          className="prob-seg"
          style={{
            width: `${probHome * 100}%`,
            background: predicted === "H" ? "#22c55e" : "#22c55e66",
          }}
        />
        <div
          className="prob-seg"
          style={{
            width: `${probDraw * 100}%`,
            background: predicted === "D" ? "#f59e0b" : "#f59e0b66",
          }}
        />
        <div
          className="prob-seg"
          style={{
            width: `${probAway * 100}%`,
            background: predicted === "A" ? "#3b82f6" : "#3b82f666",
          }}
        />
      </div>
      <div className="prob-labels">
        <span
          style={{
            color: predicted === "H" ? "#22c55e" : "var(--text-secondary)",
          }}
        >
          {(probHome * 100).toFixed(0)}%
        </span>
        <span
          style={{
            color: predicted === "D" ? "#f59e0b" : "var(--text-secondary)",
          }}
        >
          {(probDraw * 100).toFixed(0)}%
        </span>
        <span
          style={{
            color: predicted === "A" ? "#3b82f6" : "var(--text-secondary)",
          }}
        >
          {(probAway * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  );
}

function PickCard({ pick, franceOnly }) {
  const { t, i18n } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  const lc = LEAGUE_COLORS[pick.league] || "#1e293b";
  const strats = pick.strategies;
  const allOdds = pick.value_bets.filter(
    (vb) => vb.outcome === pick.predicted_result,
  );
  const singleEligible = allOdds.some((vb) => vb.odds >= 2.0);
  const visOdds = franceOnly ? allOdds.filter((vb) => vb.france_ok) : allOdds;
  const topOdds = visOdds.slice(0, 3);
  const hasOdds = topOdds.length > 0;
  const hiddenFr = franceOnly && allOdds.length > 0 && topOdds.length === 0;
  const stake = ["La_liga", "Ligue_1"].includes(pick.league) ? 30 : 10;
  const bestSingle = allOdds.filter((vb) => vb.odds >= 2.0).sort((a, b) => b.edge - a.edge)[0];
  const potential = bestSingle ? (stake * bestSingle.odds).toFixed(2) : null;

  return (
    <div className="pick-card">
      <div className="pick-header" style={{ borderLeft: `4px solid ${lc}` }}>
        <span className="pick-league">
          <Flag league={pick.league} /> {pick.league.replace("_", " ")}
        </span>
        <span className="pick-date">
          {formatDate(pick.date, i18n.language)}
        </span>
      </div>

      <div className="pick-teams">
        <span className="pick-team">{pick.home_team}</span>
        <span className="pick-vs">vs</span>
        <span className="pick-team">{pick.away_team}</span>
      </div>

      <ProbBar
        probHome={pick.prob_home}
        probDraw={pick.prob_draw}
        probAway={pick.prob_away}
        predicted={pick.predicted_result}
      />

      <div className="pick-pred-row">
        <span className="pick-pred-label">{t("bets.pred_label")}:</span>
        <span
          className="pick-pred-val"
          style={{ color: OUTCOME_COLORS[pick.predicted_result] }}
        >
          {OUTCOME_LABELS[pick.predicted_result]}
        </span>
        <div className="pick-conf">
          <div className="pick-conf-bar">
            <div
              className="pick-conf-fill"
              style={{
                width: `${pick.confidence * 100}%`,
                background: confColor(pick.confidence),
              }}
            />
          </div>
          <span
            className="pick-conf-pct"
            style={{ color: confColor(pick.confidence) }}
          >
            {(pick.confidence * 100).toFixed(0)}%
          </span>
        </div>
      </div>

      <div className="pick-badges">
        {strats.includes("elite") && (
          <span className="pick-badge badge-elite">ÉLITE</span>
        )}
        {strats.includes("alta_conf") && !strats.includes("elite") && (
          <span className="pick-badge badge-conf">ALTA CONF</span>
        )}
        {strats.includes("value_10") && (
          <span className="pick-badge badge-value10">VALUE +10%</span>
        )}
        {strats.includes("value_5") && !strats.includes("value_10") && (
          <span className="pick-badge badge-value5">VALUE +5%</span>
        )}
        {strats.length === 0 && (
          <span className="pick-badge badge-mod">
            {t("bets.conf_abbrev")} {(pick.confidence * 100).toFixed(0)}%
          </span>
        )}
        {hasOdds && !singleEligible && (
          <span className="pick-badge" style={{ background: '#475569', fontSize: '0.65rem' }}>
            {t("bets.solo_combined")}
          </span>
        )}
      </div>

      {hasOdds && (
        <div className="pick-odds">
          <div className="pick-odds-title">
            {franceOnly ? t("bets.best_odds_fr") : t("bets.best_odds")}
          </div>
          {topOdds.map((vb, i) => (
            <div key={i} className="pick-odds-row">
              <span className="po-bm">{vb.bookmaker}</span>
              <span className="po-odds">{vb.odds.toFixed(2)}</span>
              <span className="po-edge" style={{ color: edgeColor(vb.edge) }}>
                +{vb.edge.toFixed(1)}%
              </span>
              <span className="po-kelly">
                {t("bets.kelly_label")} {vb.kelly.toFixed(1)}%
              </span>
              {vb.detected_at && (
                <span style={{ marginLeft: 'auto', fontSize: '0.68rem', color: '#94a3b8' }}>
                  {vb.detected_at}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {hiddenFr && <div className="pick-no-fr">{t("bets.no_fr_odds")}</div>}

      {potential && (
        <div className="pick-stake-row">
          <span className="pick-stake-label">{t("bets.stake_action", { amount: stake })}</span>
          <span className="pick-stake-arrow">→</span>
          <span className="pick-stake-gain">€{potential} {t("bets.if_wins")}</span>
        </div>
      )}
    </div>
  );
}

function ParlayHistCard({ parlay, onBetClick }) {
  const { t } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  const typeLabel = parlay.type === "triple" ? t("bets.triple_label") : t("bets.double_label");
  const typeColor = parlay.type === "triple" ? "#fb923c" : "#a78bfa";
  const pending   = parlay.won === null || parlay.won === undefined;

  return (
    <div className={`ph-card ${parlay.won === true ? "ph-won" : parlay.won === false ? "ph-lost" : "ph-pending"}`}>
      <div className="ph-header">
        <span className="ph-type" style={{ color: typeColor }}>{typeLabel}</span>
        {parlay.bookmaker && <span className="ph-bm">{parlay.bookmaker}</span>}
        {parlay.combined_odds && (
          <span className="ph-odds">×{parlay.combined_odds.toFixed(2)}</span>
        )}
        <span className={`ph-verdict ${parlay.won === true ? "won" : parlay.won === false ? "lost" : "pending"}`}>
          {pending ? t("bets.parlay_pending") : parlay.won ? t("bets.parlay_won") : t("bets.parlay_lost")}
        </span>
        {!pending && (
          <span className="ph-pl" style={{ color: parlay.pl_flat >= 0 ? "#22c55e" : "#ef4444" }}>
            {parlay.pl_flat >= 0 ? "+" : ""}€{parlay.pl_flat?.toFixed(2)}
          </span>
        )}
      </div>
      <div className="ph-legs">
        {parlay.legs.map((leg, i) => (
          <div
            key={i}
            className="ph-leg"
            onClick={() => onBetClick && onBetClick(leg)}
            style={{ cursor: onBetClick ? "pointer" : "default" }}
          >
            <Flag league={leg.league} />
            <span className="ph-match">{leg.home_team} vs {leg.away_team}</span>
            <span className="ph-outcome" style={{ color: OUTCOME_COLORS[leg.outcome] }}>
              {OUTCOME_LABELS[leg.outcome] || leg.outcome}
            </span>
            {leg.odds && <span className="ph-leg-odds">@{leg.odds.toFixed(2)}</span>}
            <span className={`ph-leg-result ${leg.leg_won === true ? "won" : leg.leg_won === false ? "lost" : "pending"}`}>
              {leg.leg_won === true ? "✓" : leg.leg_won === false ? "✗" : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParlayHistorySection({ parlays, onBetClick }) {
  const { t } = useTranslation();
  if (!parlays || parlays.length === 0) return null;

  const validated = parlays.filter((p) => p.won !== null && p.won !== undefined);
  const pending   = parlays.filter((p) => p.won === null || p.won === undefined);
  const won       = validated.filter((p) => p.won).length;
  const pl        = validated.reduce((acc, p) => acc + (p.pl_flat || 0), 0);

  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <span className="ph-section-title">{t("bets.parlays_combined_title")}</span>
        <div className="ph-section-meta">
          {validated.length > 0 && (
            <>
              <span>{t("bets.parlays_won_of", { won, total: validated.length })}</span>
              <span style={{ color: pl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                {pl >= 0 ? "+" : ""}€{pl.toFixed(2)}
              </span>
            </>
          )}
          {pending.length > 0 && (
            <span className="ph-pending-count">{t("bets.parlays_pending_count", { n: pending.length })}</span>
          )}
        </div>
      </div>
      <div className="ph-list">
        {parlays.map((p) => <ParlayHistCard key={p.id} parlay={p} onBetClick={onBetClick} />)}
      </div>
    </div>
  );
}

function HistoryRow({ bet, onClick }) {
  const { t, i18n } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  return (
    <tr
      className={`hist-row ${bet.won ? "won" : "lost"} hist-row-clickable`}
      onClick={onClick}
    >
      <td className="hist-date">{formatDate(bet.date, i18n.language)}</td>
      <td>
        <span className="hist-league">
          <Flag league={bet.league} />
        </span>
      </td>
      <td className="hist-match">
        {bet.home_team} — {bet.away_team}
      </td>
      <td>
        <span className="hist-outcome" style={{ color: OUTCOME_COLORS[bet.outcome] }}>
          {OUTCOME_LABELS[bet.outcome]}
        </span>
      </td>
      <td className="hist-bm">{bet.bookmaker}</td>
      <td className="hist-odds">{bet.odds.toFixed(2)}</td>
      <td className="hist-edge" style={{ color: edgeColor(bet.edge) }}>
        +{bet.edge.toFixed(1)}%
      </td>
      <td className="hist-result">
        <span className={`hist-verdict ${bet.won ? "won" : "lost"}`}>
          {bet.won ? "✓" : "✗"}
        </span>
      </td>
      <td className="hist-pl" style={{ color: plColor(bet.pl_flat) }}>
        {bet.pl_flat >= 0 ? "+" : ""}€{bet.pl_flat.toFixed(2)}
      </td>
    </tr>
  );
}

function BetDetailModal({ bet, onClose }) {
  const { t, i18n } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  const lc = LEAGUE_COLORS[bet.league] || "#1e293b";
  const isSameAsPredicted = bet.outcome === bet.predicted_result;

  const outcomeLabel = OUTCOME_LABELS[bet.outcome] || bet.outcome;
  const predictedLabel = bet.predicted_result ? (OUTCOME_LABELS[bet.predicted_result] || bet.predicted_result) : null;

  const hasMetrics = bet.edge != null && bet.prob_model != null && bet.prob_implied != null;
  const explanation = hasMetrics
    ? (isSameAsPredicted
      ? t("bets.explanation_same", { outcome: outcomeLabel, implied: bet.prob_implied, model: bet.prob_model, edge: bet.edge.toFixed(1) })
      : t("bets.explanation_diff", { predicted: predictedLabel, outcome: outcomeLabel, implied: bet.prob_implied, model: bet.prob_model, edge: bet.edge.toFixed(1) }))
    : null;

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const actualLabel = bet.actual_result ? (OUTCOME_LABELS[bet.actual_result] || bet.actual_result) : null;

  return (
    <div className="bet-modal-overlay" onClick={onClose}>
      <div className="bet-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="bet-modal-header" style={{ borderLeft: `4px solid ${lc}` }}>
          <div className="bm-header-left">
            <span className="bm-league-tag">
              <Flag league={bet.league} /> {bet.league.replace("_", " ")}
            </span>
            <span className="bm-header-date">{formatDate(bet.date, i18n.language)}</span>
          </div>
          <button className="bm-close" onClick={onClose}>✕</button>
        </div>

        <div className="bet-modal-body">
          {/* Teams + result */}
          <div className="bm-teams-row">
            <div className="bm-teams">
              <span className="bm-team">{bet.home_team}</span>
              {bet.home_score != null && bet.away_score != null ? (
                <span className="bm-score">{bet.home_score} – {bet.away_score}</span>
              ) : (
                <span className="bm-vs">vs</span>
              )}
              <span className="bm-team">{bet.away_team}</span>
            </div>
            <div className="bm-result-badges">
              <span className={`bm-verdict-badge ${bet.won ? "won" : "lost"}`}>
                {bet.won ? t("bets.modal_won") : t("bets.modal_lost")}
              </span>
              {actualLabel && (
                <span className="bm-actual-badge">
                  {t("bets.actual_result_label")} <strong>{actualLabel}</strong>
                </span>
              )}
              {bet.pl_flat != null && (
                <span className="bm-pl-badge" style={{ color: plColor(bet.pl_flat) }}>
                  {bet.pl_flat >= 0 ? "+" : ""}€{Math.abs(bet.pl_flat).toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* Model prediction */}
          {bet.prob_home != null && (
            <div className="bm-section">
              <div className="bm-section-title">{t("bets.model_prediction_title")}</div>
              <ProbBar
                probHome={bet.prob_home / 100}
                probDraw={bet.prob_draw / 100}
                probAway={bet.prob_away / 100}
                predicted={bet.predicted_result}
              />
              <div className="bm-pred-detail">
                <span className="bm-pred-label">{t("bets.favorite_label")}</span>
                <span
                  className="bm-pred-val"
                  style={{ color: OUTCOME_COLORS[bet.predicted_result] }}
                >
                  {predictedLabel}
                </span>
                {bet.confidence != null && (
                  <span className="bm-conf-tag">{bet.confidence.toFixed(1)}{t("bets.confidence_tag")}</span>
                )}
              </div>
            </div>
          )}

          {/* Value metrics */}
          <div className="bm-section">
            <div className="bm-section-title">{t("bets.bet_detail_title")}</div>
            <div className="bm-detail-grid">
              <div className="bm-detail-item">
                <span className="bm-detail-label">{t("bets.bet_placed_label")}</span>
                <span className="bm-detail-val" style={{ color: OUTCOME_COLORS[bet.outcome] }}>
                  {outcomeLabel}
                  {!isSameAsPredicted && (
                    <span className="bm-contrarian-tag"> {t("bets.not_favorite_tag")}</span>
                  )}
                </span>
              </div>
              <div className="bm-detail-item">
                <span className="bm-detail-label">{t("bets.bookmaker_label")}</span>
                <span className="bm-detail-val">{bet.bookmaker}</span>
              </div>
              <div className="bm-detail-item">
                <span className="bm-detail-label">{t("bets.odds_label")}</span>
                <span className="bm-detail-val bm-odds-val">×{bet.odds.toFixed(2)}</span>
              </div>
            </div>
            {hasMetrics && (
              <div className="bm-metrics">
                <div className="bm-metric">
                  <div className="bm-metric-val" style={{ color: "#3b82f6" }}>{bet.prob_model}%</div>
                  <div className="bm-metric-label">{t("bets.prob_model_label")}</div>
                </div>
                <div className="bm-metric-sep">→</div>
                <div className="bm-metric">
                  <div className="bm-metric-val" style={{ color: "#94a3b8" }}>{bet.prob_implied}%</div>
                  <div className="bm-metric-label">{t("bets.prob_implied_label")}</div>
                </div>
                <div className="bm-metric-sep">=</div>
                <div className="bm-metric">
                  <div className="bm-metric-val" style={{ color: edgeColor(bet.edge) }}>
                    +{bet.edge.toFixed(1)}%
                  </div>
                  <div className="bm-metric-label">{t("bets.edge_label")}</div>
                </div>
                {bet.kelly != null && (
                  <div className="bm-metric">
                    <div className="bm-metric-val" style={{ color: "#a78bfa" }}>
                      {bet.kelly.toFixed(1)}%
                    </div>
                    <div className="bm-metric-label">Kelly</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Explanation */}
          {explanation && (
            <div className="bm-explanation">
              <p className="bm-exp-text">{explanation}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── JORNADA COMPONENTS ───────────────────────────────────────

function BetInstruction({ pick, franceOnly, index }) {
  const { t, i18n } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  const vbs = pick.value_bets.filter(
    (v) => v.outcome === pick.predicted_result && v.odds >= 2.0,
  );
  const eligible = franceOnly ? vbs.filter((v) => v.france_ok) : vbs;
  const top = (eligible.length > 0 ? eligible : vbs)[0];
  if (!top) return null;

  const isTopLeague = ["La_liga", "Ligue_1"].includes(pick.league);
  const stake = isTopLeague ? 30 : 10;
  const stakeLabel = isTopLeague
    ? `€${stake} (${t("bets.stake_pct_bk")})`
    : `€${stake}`;
  const potential = (stake * top.odds).toFixed(2);

  return (
    <div className="bet-instr">
      <div className="bi-num">{index}</div>
      <div className="bi-body">
        <div className="bi-top">
          <span className="bi-league">
            <Flag league={pick.league} /> {pick.league.replace("_", " ")}
          </span>
          <span className="bi-date">
            {formatDate(pick.date, i18n.language)}
          </span>
          <span
            className="bi-conf"
            style={{ color: confColor(pick.confidence) }}
          >
            {(pick.confidence * 100).toFixed(0)}% {t("bets.conf_abbrev")}
          </span>
        </div>
        <div className="bi-match">
          {pick.home_team} vs {pick.away_team}
        </div>
        <div className="bi-action">
          <span
            className="bi-outcome"
            style={{ color: OUTCOME_COLORS[pick.predicted_result] }}
          >
            {OUTCOME_LABELS[pick.predicted_result]}
          </span>
          <span className="bi-at"> {t("bets.in_at")} </span>
          <span className="bi-bm">{top.bookmaker}</span>
          <span className="bi-odds"> @ {top.odds.toFixed(2)}</span>
        </div>
        <div className="bi-meta">
          <span className="bi-stake">{stakeLabel}</span>
          <span className="bi-edge" style={{ color: edgeColor(top.edge) }}>
            +{top.edge.toFixed(1)}% {t("bets.edge_label")}
          </span>
          <span className="bi-gain">
            → €{potential} {t("bets.if_wins")}
          </span>
        </div>
      </div>
    </div>
  );
}

function ParlaySlip({ parlay, type = "double" }) {
  const { t } = useTranslation();
  const OUTCOME_LABELS = {
    H: t("bets.outcome_home"),
    D: t("bets.outcome_draw"),
    A: t("bets.outcome_away"),
  };
  if (!parlay) return null;
  const isTriple = type === "triple";
  const stake = 10;
  const potential = parlay.combined_odds
    ? (stake * parlay.combined_odds).toFixed(2)
    : null;

  return (
    <div className={`parlay-slip${isTriple ? " ps-triple" : ""}`}>
      <div className="ps-title">
        {isTriple ? t("bets.triple_label") : t("bets.double_label")}
        {parlay.bookmaker ? (
          <span className="ps-bm ps-bm-main">{parlay.bookmaker}</span>
        ) : (
          <span className="ps-no-bm">{t("bets.no_bm_long")}</span>
        )}
      </div>
      {parlay.legs.map((leg, i) => (
        <div key={i} className="ps-leg">
          <span className="ps-num">{i + 1}</span>
          <Flag league={leg.league} />
          <span className="ps-match">
            {leg.home_team} vs {leg.away_team}
          </span>
          <span
            className="ps-outcome"
            style={{ color: OUTCOME_COLORS[leg.predicted_result] }}
          >
            {OUTCOME_LABELS[leg.predicted_result]}
          </span>
          {leg.odds > 0 && (
            <span className="ps-odds">@{leg.odds.toFixed(2)}</span>
          )}
        </div>
      ))}
      <div className="ps-footer">
        {parlay.combined_odds && (
          <span>
            {t("bets.combined_odds")}{" "}
            <strong>{parlay.combined_odds.toFixed(2)}</strong>
          </span>
        )}
        <span>
          {t("bets.stake_label")} <strong>€{stake}</strong>
        </span>
        {potential && (
          <span className="ps-gain">
            → <strong>€{potential}</strong> {t("bets.if_wins")}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Tennis value bets ─────────────────────────────────────────

const SURFACE_COLORS = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };

function TennisBetCard({ bet }) {
  const { t, i18n } = useTranslation();
  const edgePct    = bet.edge.toFixed(1);
  const kellyPct   = bet.kelly.toFixed(1);
  const modelPct   = (bet.prob_model * 100).toFixed(1);
  const impliedPct = (bet.prob_implied * 100).toFixed(1);
  const surfColor  = SURFACE_COLORS[bet.surface] || '#6b7280';
  const stake      = 10;
  const potential  = bet.odds ? (stake * bet.odds).toFixed(2) : null;
  return (
    <div className="pick-card">
      <div className="pick-header" style={{ borderLeft: `4px solid ${surfColor}` }}>
        <span className="pick-league">{bet.tournament} · {bet.level_name}</span>
        <span className="pick-date">{formatDate(bet.date, i18n.language)}</span>
      </div>
      <div className="pick-teams">
        <span className="pick-team">{bet.p1_name}</span>
        <span className="pick-vs">vs</span>
        <span className="pick-team">{bet.p2_name}</span>
      </div>
      <div className="prob-bar-wrap">
        <div className="prob-bar">
          <div className="prob-seg" style={{ width: `${bet.prob_model * 100}%`, background: '#22c55e' }} />
          <div className="prob-seg" style={{ width: `${(1 - bet.prob_model) * 100}%`, background: '#3b82f6' }} />
        </div>
        <div className="prob-labels">
          <span style={{ color: '#22c55e' }}>{modelPct}%</span>
          <span style={{ color: '#3b82f6' }}>{(100 - parseFloat(modelPct)).toFixed(1)}%</span>
        </div>
      </div>
      <div className="pick-pred-row">
        <span className="pick-pred-label">{t('bets.pred_label')}:</span>
        <span className="pick-pred-val" style={{ color: '#22c55e' }}>{bet.player_name}</span>
        <span className="pick-conf" style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 8 }}>
          @ {bet.bookmaker} · odds {bet.odds?.toFixed(2)}
        </span>
      </div>
      <div className="pick-odds">
        <div className="pick-odds-title">Value metrics</div>
        <div className="pick-odds-row">
          <span className="po-bm">Model prob</span>
          <span className="po-odds" style={{ color: '#3b82f6' }}>{modelPct}%</span>
          <span className="po-edge" style={{ color: edgeColor(bet.edge) }}>+{edgePct}% edge</span>
          <span className="po-kelly">Kelly {kellyPct}%</span>
        </div>
        <div className="pick-odds-row" style={{ fontSize: '0.75rem', color: '#64748b' }}>
          <span>Implied: {impliedPct}%</span>
          <span style={{ marginLeft: 8 }}>
            <span style={{ background: surfColor, color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: '0.7rem' }}>
              {bet.surface}
            </span>
          </span>
          {bet.detected_at && (
            <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.7rem' }}>
              {t("bets.detected_at", { time: bet.detected_at })}
            </span>
          )}
        </div>
      </div>
      {potential && (
        <div className="pick-stake-row">
          <span className="pick-stake-label">{t("bets.stake_action", { amount: stake })}</span>
          <span className="pick-stake-arrow">→</span>
          <span className="pick-stake-gain">€{potential} {t("bets.if_wins")}</span>
        </div>
      )}
    </div>
  );
}

function BaseballBetCard({ bet }) {
  const { t, i18n } = useTranslation();
  const edgePct    = (bet.edge * 100).toFixed(1);
  const kellyPct   = (bet.kelly * 100).toFixed(1);
  const modelPct   = (bet.prob_model * 100).toFixed(1);
  const impliedPct = (bet.prob_implied * 100).toFixed(1);
  const isPending  = bet.won === null || bet.won === undefined;
  const stake      = 10;
  const potential  = bet.odds ? (stake * bet.odds).toFixed(2) : null;
  return (
    <div className="pick-card">
      <div className="pick-header" style={{ borderLeft: '4px solid #1d4ed8' }}>
        <span className="pick-league">MLB Baseball</span>
        <span className="pick-date">{formatDate(bet.game_date, i18n.language)}</span>
      </div>
      <div className="pick-teams">
        <span className="pick-team">{bet.home_team}</span>
        <span className="pick-vs">vs</span>
        <span className="pick-team">{bet.away_team}</span>
      </div>
      <div className="prob-bar-wrap">
        <div className="prob-bar">
          <div className="prob-seg" style={{ width: `${bet.prob_model * 100}%`, background: '#22c55e' }} />
          <div className="prob-seg" style={{ width: `${(1 - bet.prob_model) * 100}%`, background: '#3b82f6' }} />
        </div>
        <div className="prob-labels">
          <span style={{ color: '#22c55e' }}>{modelPct}%</span>
          <span style={{ color: '#3b82f6' }}>{(100 - parseFloat(modelPct)).toFixed(1)}%</span>
        </div>
      </div>
      <div className="pick-pred-row">
        <span className="pick-pred-label">{t('bets.pred_label')}:</span>
        <span className="pick-pred-val" style={{ color: '#22c55e' }}>{bet.team_name}</span>
        <span className="pick-conf" style={{ fontSize: '0.8rem', color: '#64748b', marginLeft: 8 }}>
          @ {bet.bookmaker} · odds {bet.odds?.toFixed(2)}
        </span>
      </div>
      <div className="pick-odds">
        <div className="pick-odds-title">Value metrics</div>
        <div className="pick-odds-row">
          <span className="po-bm">Model prob</span>
          <span className="po-odds" style={{ color: '#3b82f6' }}>{modelPct}%</span>
          <span className="po-edge" style={{ color: edgeColor(bet.edge * 100) }}>+{edgePct}% edge</span>
          <span className="po-kelly">Kelly {kellyPct}%</span>
        </div>
        <div className="pick-odds-row" style={{ fontSize: '0.75rem', color: '#64748b' }}>
          <span>Implied: {impliedPct}%</span>
          {!isPending && (
            <span style={{ color: bet.won ? '#22c55e' : '#ef4444', fontWeight: 600, marginLeft: 8 }}>
              {bet.won ? '✓ Won' : '✗ Lost'}
            </span>
          )}
          {bet.detected_at && (
            <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.7rem' }}>
              {t("bets.detected_at", { time: bet.detected_at })}
            </span>
          )}
        </div>
      </div>
      {potential && isPending && (
        <div className="pick-stake-row">
          <span className="pick-stake-label">{t("bets.stake_action", { amount: stake })}</span>
          <span className="pick-stake-arrow">→</span>
          <span className="pick-stake-gain">€{potential} {t("bets.if_wins")}</span>
        </div>
      )}
    </div>
  );
}

function TennisValueBetsContent() {
  const { t, i18n } = useTranslation();
  const [bets, setBets]       = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/tennis/value-bets`);
      const data = await res.json();
      setBets(data.value_bets || []);
    } catch (e) {
      console.error(e);
      setBets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  if (loading) return <div className="bets-loading">{t('bets.loading')}</div>;

  if (bets.length === 0) {
    return (
      <div className="bets-empty">
        <div className="bets-empty-icon">—</div>
        <div>{t("bets.no_tennis_bets")}</div>
        <div className="bets-empty-sub">{t("bets.no_tennis_bets_sub")}</div>
      </div>
    );
  }

  return (
    <div className="bets-picks">
      <div className="picks-controls">
        <span className="picks-count">{bets.length} value bets · {t("tennis.page_title")}</span>
        <button className="refresh-button" onClick={load}>{t('common.refresh')}</button>
      </div>

      {bets.map((bet, i) => <TennisBetCard key={i} bet={bet} />)}
    </div>
  );
}

// ── Baseball value bets ───────────────────────────────────────

function BaseballValueBetsContent() {
  const { t, i18n } = useTranslation();
  const [bets, setBets]       = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/baseball/value-bets`);
      const data = await res.json();
      setBets(data || []);
    } catch (e) {
      console.error(e);
      setBets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="bets-loading">{t('bets.loading')}</div>;
  }

  if (bets.length === 0) {
    return (
      <div className="bets-empty">
        <div className="bets-empty-icon">—</div>
        <div>{t("bets.no_baseball_bets")}</div>
        <div className="bets-empty-sub">{t("bets.no_tennis_bets_sub")}</div>
      </div>
    );
  }

  return (
    <div className="bets-picks">
      <div className="picks-controls">
        <span className="picks-count">{bets.length} value bets · MLB</span>
        <button className="refresh-button" onClick={load}>{t('common.refresh')}</button>
      </div>

      {bets.map((bet, i) => <BaseballBetCard key={i} bet={bet} />)}
    </div>
  );
}

// ── Global Balance ────────────────────────────────────────────

function GlobalBalance({ onSportClick }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API}/bets/global-balance`)
      .then(r => r.json()).then(setData).catch(() => {});
  }, []);
  if (!data) return null;
  const sports = [
    { key: "football", label: t("bets.sport_football"), data: data.by_sport?.find(s => s.sport === "football") },
    { key: "tennis",   label: t("bets.sport_tennis"),   data: data.by_sport?.find(s => s.sport === "tennis") },
    { key: "baseball", label: t("bets.sport_baseball"), data: data.by_sport?.find(s => s.sport === "baseball") },
  ];
  return (
    <div className="global-balance-bar">
      <div className="gb-total">
        <span className="gb-label">{t("bets.global_balance")}</span>
        <span className="gb-pl" style={{ color: plColor(data.total_pl) }}>
          {data.total_pl >= 0 ? "+" : ""}€{data.total_pl?.toFixed(2)}
        </span>
        <span className="gb-roi" style={{ color: plColor(data.roi_global) }}>
          ROI {data.roi_global >= 0 ? "+" : ""}{data.roi_global?.toFixed(1)}%
        </span>
      </div>
      <div className="gb-sports">
        {sports.map(s => s.data && (
          <button key={s.key} className="gb-sport-pill" onClick={() => onSportClick(s.key)}>
            <span>{s.label}</span>
            <span style={{ color: plColor(s.data.combined_pl) }}>
              {s.data.combined_pl >= 0 ? "+" : ""}€{s.data.combined_pl?.toFixed(0)}
            </span>
            <span className="gb-roi-small" style={{ color: plColor(s.data.roi) }}>
              {s.data.roi >= 0 ? "+" : ""}{s.data.roi?.toFixed(1)}%
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Tennis Pick Card (weekly-picks format) ────────────────────

function TennisPickCard({ pick, franceOnly }) {
  const { t, i18n } = useTranslation();
  const SURFACE_COLORS = { Hard: "#3b82f6", Clay: "#f97316", Grass: "#22c55e" };
  const surfColor = SURFACE_COLORS[pick.surface] || "#6b7280";
  const LEVEL_COLOR = { G: "#f59e0b", M: "#a78bfa", F: "#ec4899", A: "#64748b" };
  const lvlColor = LEVEL_COLOR[pick.level] || "#64748b";
  const relevant = (pick.value_bets || []).filter(v =>
    v.outcome === pick.predicted_outcome && (!franceOnly || v.france_ok)
  );
  const bestVb = relevant.sort((a, b) => b.odds - a.odds)[0];
  const potential = bestVb ? (pick.stake * bestVb.odds).toFixed(2) : null;
  return (
    <div className="pick-card">
      <div className="pick-header" style={{ borderLeft: `4px solid ${surfColor}` }}>
        <span className="pick-league" style={{ color: lvlColor }}>
          {pick.tournament} · {pick.level_name}
        </span>
        <span className="pick-date">{formatDate(pick.date, i18n.language)}</span>
      </div>
      <div className="pick-teams">
        <span className={`pick-team ${pick.predicted_outcome === "p1" ? "pick-team-predicted" : ""}`}>
          {pick.p1_name}
        </span>
        <span className="pick-vs">vs</span>
        <span className={`pick-team ${pick.predicted_outcome === "p2" ? "pick-team-predicted" : ""}`}>
          {pick.p2_name}
        </span>
      </div>
      <div className="prob-bar-wrap">
        <div className="prob-bar">
          <div className="prob-seg" style={{ width: `${pick.prob_p1 * 100}%`, background: "#22c55e" }} />
          <div className="prob-seg" style={{ width: `${pick.prob_p2 * 100}%`, background: "#3b82f6" }} />
        </div>
        <div className="prob-labels">
          <span style={{ color: "#22c55e" }}>{(pick.prob_p1 * 100).toFixed(0)}%</span>
          <span style={{ color: "#3b82f6" }}>{(pick.prob_p2 * 100).toFixed(0)}%</span>
        </div>
      </div>
      <div className="pick-pred-row">
        <span className="pick-pred-label">{t("bets.pred_label")}:</span>
        <span className="pick-pred-val" style={{ color: "#22c55e" }}>{pick.predicted_player}</span>
        <span className="pick-conf">{t("bets.conf_label")} {(pick.confidence * 100).toFixed(0)}%</span>
      </div>
      {relevant.length > 0 && (
        <div className="pick-odds">
          <div className="pick-odds-title">Value bets</div>
          {relevant.slice(0, 3).map((vb, i) => (
            <div key={i} className="pick-odds-row">
              <span className="po-bm">{vb.bookmaker}</span>
              <span className="po-odds">{vb.odds.toFixed(2)}</span>
              <span className="po-edge" style={{ color: edgeColor(vb.edge) }}>+{vb.edge.toFixed(1)}%</span>
              <span className="po-kelly">Kelly {vb.kelly.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
      {potential && (
        <div className="pick-stake-row">
          <span className="pick-stake-label">{t("bets.stake_action", { amount: pick.stake })}</span>
          <span className="pick-stake-arrow">→</span>
          <span className="pick-stake-gain">€{potential} {t("bets.if_wins")}</span>
        </div>
      )}
    </div>
  );
}

// ── Tennis/Baseball Parlay Card ───────────────────────────────

function SportParlayCard({ parlay, type = "double", sport = "tennis" }) {
  const { t } = useTranslation();
  if (!parlay) return null;
  const { legs, combined_odds, combined_prob, bookmaker } = parlay;
  const isTriple = type === "triple";
  return (
    <div className={`parlay-card${isTriple ? " parlay-triple" : ""}`}>
      <div className="parlay-header">
        <span className="parlay-title">{isTriple ? t("bets.triple_label") : t("bets.double_label")}</span>
        {bookmaker
          ? <span className="pl-bm parlay-bm">{bookmaker}</span>
          : <span className="parlay-no-bm">{t("bets.no_bm")}</span>}
      </div>
      <div className="parlay-legs">
        {legs.map((leg, i) => (
          <div key={i} className="parlay-leg">
            <span className="pl-match">
              {sport === "tennis"
                ? `${leg.p1_name} vs ${leg.p2_name} → ${leg.predicted_player || leg.predicted_outcome}`
                : `${leg.home_team} vs ${leg.away_team} → ${leg.predicted_team || leg.predicted_winner}`}
            </span>
            <span className="pl-odds">{leg.odds?.toFixed(2)}</span>
            {leg.best_edge > 0 && (
              <span className="pl-edge" style={{ color: edgeColor(leg.best_edge) }}>
                +{leg.best_edge.toFixed(1)}%
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="parlay-footer">
        {combined_odds && (
          <span className="parlay-combined">
            {t("bets.combined_odds")}: <strong>{combined_odds.toFixed(2)}</strong>
          </span>
        )}
        {combined_prob && (
          <span className="parlay-prob">
            {t("bets.model_prob")}: {(combined_prob * 100).toFixed(1)}%
          </span>
        )}
        <span>{t("bets.stake_label")} <strong>€10</strong></span>
        {combined_odds && (
          <span className="ps-gain">→ <strong>€{(10 * combined_odds).toFixed(2)}</strong> {t("bets.if_wins")}</span>
        )}
      </div>
    </div>
  );
}

// ── Tennis History Row ────────────────────────────────────────

function TennisHistoryRow({ bet }) {
  const { i18n } = useTranslation();
  const SURFACE_COLORS = { Hard: "#3b82f6", Clay: "#f97316", Grass: "#22c55e" };
  return (
    <tr className={`hist-row ${bet.won ? "won" : "lost"}`}>
      <td className="hist-date">{formatDate(bet.date, i18n.language)}</td>
      <td>
        <span style={{ color: SURFACE_COLORS[bet.surface] || "#6b7280", fontSize: "0.75rem" }}>
          {bet.surface}
        </span>
      </td>
      <td className="hist-match">{bet.p1_name} — {bet.p2_name}</td>
      <td><span className="hist-bm">{bet.player_name}</span></td>
      <td className="hist-bm">{bet.bookmaker}</td>
      <td className="hist-odds">{bet.odds?.toFixed(2)}</td>
      <td className="hist-edge" style={{ color: edgeColor(bet.edge) }}>+{bet.edge.toFixed(1)}%</td>
      <td><span className={`hist-verdict ${bet.won ? "won" : "lost"}`}>{bet.won ? "✓" : "✗"}</span></td>
      <td className="hist-pl" style={{ color: plColor(bet.pl_flat) }}>
        {bet.pl_flat >= 0 ? "+" : ""}€{bet.pl_flat?.toFixed(2)}
      </td>
    </tr>
  );
}

// ── Baseball History Row ──────────────────────────────────────

function BaseballHistoryRow({ bet }) {
  const { t, i18n } = useTranslation();
  const outcomeLabel = bet.outcome === "home" ? t("bets.outcome_home") : bet.outcome === "away" ? t("bets.outcome_away") : bet.outcome;
  return (
    <tr className={`hist-row ${bet.won ? "won" : "lost"}`}>
      <td className="hist-date">{formatDate(bet.date, i18n.language)}</td>
      <td style={{ fontSize: "0.75rem", color: "#64748b" }}>MLB</td>
      <td className="hist-match">{bet.home_team} — {bet.away_team}</td>
      <td><span className="hist-outcome">{outcomeLabel} ({bet.team_name})</span></td>
      <td className="hist-bm">{bet.bookmaker}</td>
      <td className="hist-odds">{bet.odds?.toFixed(2)}</td>
      <td className="hist-edge" style={{ color: edgeColor(bet.edge) }}>+{bet.edge.toFixed(1)}%</td>
      <td><span className={`hist-verdict ${bet.won ? "won" : "lost"}`}>{bet.won ? "✓" : "✗"}</span></td>
      <td className="hist-pl" style={{ color: plColor(bet.pl_flat) }}>
        {bet.pl_flat >= 0 ? "+" : ""}€{bet.pl_flat?.toFixed(2)}
      </td>
    </tr>
  );
}

// ── Tennis Arb Card ───────────────────────────────────────────

function TennisArbCard({ arb }) {
  const levelColors  = { G: '#f59e0b', M: '#a855f7', F: '#ef4444', A: '#3b82f6' };
  const surfaceColor = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e' };
  return (
    <div className="pick-card arb-card">
      <div className="pick-header" style={{ borderLeft: `4px solid ${levelColors[arb.tourney_level] || '#334155'}` }}>
        <span className="pick-league" style={{ background: surfaceColor[arb.surface] || '#334155', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: '0.72rem' }}>
          {arb.surface}
        </span>
        <span style={{ flex: 1, fontSize: '0.78rem', marginLeft: '0.5rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {arb.tourney_name}
        </span>
        <span className="pick-date">{arb.date}</span>
      </div>
      <div className="pick-teams">
        <span className="pick-team">{arb.p1_name}</span>
        <span className="pick-vs">vs</span>
        <span className="pick-team">{arb.p2_name}</span>
      </div>
      <div style={{ textAlign: 'center', margin: '6px 0', fontWeight: 700, fontSize: '1rem',
          color: arb.arb_margin >= 0 ? '#22c55e' : '#ef4444' }}>
        ARB {arb.arb_margin >= 0 ? '+' : ''}{arb.arb_margin?.toFixed(2)}%
        <span style={{ fontWeight: 400, fontSize: '0.78rem', color: '#94a3b8', marginLeft: 8 }}>
          +€{arb.guaranteed_profit?.toFixed(2)} garantizado / €{arb.total_stake}
        </span>
      </div>
      <div className="pick-odds">
        <div className="pick-odds-row" style={{ background: 'rgba(34,197,94,0.08)', borderRadius: 4, padding: '5px 8px', marginBottom: 4 }}>
          <span className="po-bm">{arb.bm_p1}</span>
          <span className="po-odds" style={{ fontWeight: 700 }}>{arb.odds_p1?.toFixed(2)}</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>
            {arb.p1_name?.split(' ').pop()} →
            <span style={{ color: '#22c55e', fontWeight: 600 }}> €{arb.stake_p1}</span>
          </span>
        </div>
        <div className="pick-odds-row" style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 4, padding: '5px 8px' }}>
          <span className="po-bm">{arb.bm_p2}</span>
          <span className="po-odds" style={{ fontWeight: 700 }}>{arb.odds_p2?.toFixed(2)}</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem' }}>
            {arb.p2_name?.split(' ').pop()} →
            <span style={{ color: '#3b82f6', fontWeight: 600 }}> €{arb.stake_p2}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Tennis Tab ────────────────────────────────────────────────

function TennisTab() {
  const [subtab, setSubtab]   = useState("picks");
  const [picks, setPicks]     = useState([]);
  const [doubles, setDoubles] = useState([]);
  const [triples, setTriples] = useState([]);
  const [history, setHistory] = useState([]);
  const [histTotal, setHistTotal] = useState(0);
  const [histPage, setHistPage]   = useState(0);
  const [stats, setStats]         = useState(null);
  const [arbs, setArbs]           = useState([]);
  const [arbStake, setArbStake]   = useState(100);
  const [loading, setLoading]     = useState(false);
  const [franceOnly, setFranceOnly] = useState(true);
  const HIST_LIMIT = 50;

  const loadPicks = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/tennis/weekly-picks?days=14&min_confidence=0.6`);
      const data = await res.json();
      setPicks(data.picks || []);
      setDoubles(data.doubles || []);
      setTriples(data.triples || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/tennis/bets-history?limit=${HIST_LIMIT}&offset=${page * HIST_LIMIT}`);
      const data = await res.json();
      setHistory(data.bets || []);
      setHistTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/tennis/bets-stats`);
      setStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadArbs = useCallback(async (stake = arbStake) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/tennis/arbitrage?total_stake=${stake}`);
      const data = await res.json();
      setArbs(data.opportunities || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [arbStake]);

  useEffect(() => {
    if (subtab === "picks")   loadPicks();
    if (subtab === "history") loadHistory(histPage);
    if (subtab === "stats")   loadStats();
    if (subtab === "arb")     loadArbs();
  }, [subtab, histPage]);

  const { t } = useTranslation();

  const SUBTABS = [
    { key: "picks",   label: "Picks" },
    { key: "arb",     label: t("bets.tennis_subtab_arb") },
    { key: "history", label: t("bets.subtab_history") },
    { key: "stats",   label: t("bets.tennis_subtab_stats") },
  ];

  return (
    <div>
      <div className="bets-subtabs">
        {SUBTABS.map(s => (
          <button key={s.key} className={`bets-subtab ${subtab === s.key ? "active" : ""}`}
            onClick={() => setSubtab(s.key)}>{s.label}</button>
        ))}
        <button className={`france-toggle ${franceOnly ? "active" : ""}`}
          style={{ marginLeft: "auto" }} onClick={() => setFranceOnly(f => !f)}>
          {t("bets.france_only_short")}
        </button>
      </div>

      {loading && <div className="bets-loading">{t("common.loading")}</div>}

      {subtab === "picks" && !loading && (
        <div>
          {picks.length === 0 ? (
            <div className="bets-empty">
              <div className="bets-empty-icon">—</div>
              <div>{t("bets.no_tennis_picks")}</div>
            </div>
          ) : (
            <div>
              <div className="jornada-section">
                <div className="jsec-title">{t("bets.singles_tennis")}</div>
                <div className="picks-grid">
                  {picks.filter(p => p.best_edge >= 5).map((p, i) => (
                    <TennisPickCard key={i} pick={p} franceOnly={franceOnly} />
                  ))}
                </div>
              </div>
              {doubles.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">{t("bets.doubles_section")}</div>
                  <div className="parlay-grid">
                    {doubles.slice(0, 3).map((d, i) => (
                      <SportParlayCard key={i} parlay={d} type="double" sport="tennis" />
                    ))}
                  </div>
                </div>
              )}
              {triples.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">{t("bets.triples_section")}</div>
                  <div className="parlay-grid">
                    {triples.map((tr, i) => (
                      <SportParlayCard key={i} parlay={tr} type="triple" sport="tennis" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {subtab === "arb" && !loading && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 0 16px' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{t("bets.arb_stake_label")}</span>
            <input
              type="number" min="10" max="10000" step="10"
              value={arbStake}
              onChange={e => setArbStake(Number(e.target.value))}
              style={{ width: 80, padding: '4px 8px', borderRadius: 6,
                border: '1px solid #334155', background: '#1e293b', color: '#f1f5f9' }}
            />
            <button className="bets-subtab active" style={{ padding: '4px 14px' }}
              onClick={() => loadArbs(arbStake)}>{t("bets.arb_recalc")}</button>
            <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.8rem' }}>
              {arbs.length === 1
                ? t("bets.arb_count", { n: arbs.length })
                : t("bets.arb_count_pl", { n: arbs.length })}
            </span>
          </div>
          {arbs.length === 0 ? (
            <div className="bets-empty">
              <div className="bets-empty-icon">—</div>
              <div>{t("bets.no_arb")}</div>
              <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: 6 }}>
                {t("bets.arb_desc")}
              </div>
            </div>
          ) : (
            <div className="picks-grid">
              {arbs.map((a, i) => <TennisArbCard key={i} arb={a} />)}
            </div>
          )}
        </div>
      )}

      {subtab === "history" && !loading && (
        <div>
          {history.length === 0 ? (
            <div className="bets-empty"><div className="bets-empty-icon">—</div><div>{t("bets.no_history_yet")}</div></div>
          ) : (
            <div className="hist-table-wrap">
              <div className="hist-meta">
                {t("bets.history_page_meta", { total: histTotal, page: histPage + 1, pages: Math.ceil(histTotal / HIST_LIMIT) })}
              </div>
              <table className="hist-table">
                <thead>
                  <tr>
                    <th>{t("bets.hist_col_date")}</th><th>{t("bets.col_surf")}</th><th>{t("bets.hist_col_match")}</th><th>{t("bets.col_pick")}</th>
                    <th>{t("bets.hist_col_bm")}</th><th>{t("bets.hist_col_odds")}</th><th>{t("bets.hist_col_edge")}</th><th>{t("bets.hist_col_res")}</th><th>{t("bets.hist_col_pl")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b, i) => <TennisHistoryRow key={i} bet={b} />)}
                </tbody>
              </table>
              <div className="hist-pagination">
                <button disabled={histPage === 0} onClick={() => setHistPage(p => p - 1)}>{t("bets.hist_prev")}</button>
                <button disabled={(histPage + 1) * HIST_LIMIT >= histTotal} onClick={() => setHistPage(p => p + 1)}>{t("bets.hist_next")}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {subtab === "stats" && !loading && stats?.global && (
        <div className="stats-section-wrap">
          <div className="stats-cards-grid">
            <StatCard label="P&L Total" value={`${stats.global.combined_pl >= 0 ? "+" : ""}€${stats.global.combined_pl?.toFixed(2)}`}
              sub={`ROI ${stats.global.roi_flat >= 0 ? "+" : ""}${stats.global.roi_flat?.toFixed(1)}%`} color={plColor(stats.global.combined_pl)} />
            <StatCard label="Win Rate" value={`${stats.global.win_rate?.toFixed(1)}%`}
              sub={`${stats.global.won_bets}/${stats.global.total_bets} bets`} color="#f59e0b" />
            <StatCard label="Avg Edge" value={`+${stats.global.avg_edge?.toFixed(1)}%`}
              sub={`${t("bets.stats_avg_odds_sub", { odds: stats.global.avg_odds?.toFixed(2) })}`} color="#3b82f6" />
            <StatCard label="Bankroll" value={`€${stats.global.combined_bankroll?.toFixed(0)}`}
              sub="Base €1000" color="#22c55e" />
          </div>
          {stats.by_surface?.length > 0 && (
            <div className="stats-section">
              <h3 className="stats-section-title">{t("bets.by_surface")}</h3>
              <div className="stats-league-grid">
                {stats.by_surface.map(s => (
                  <div key={s.surface} className="stats-league-card"
                    style={{ borderLeft: `4px solid ${({ Hard: "#3b82f6", Clay: "#f97316", Grass: "#22c55e" }[s.surface] || "#334155")}` }}>
                    <div className="slc-header">
                      <span>{s.surface}</span>
                      <span style={{ color: plColor(s.pl_flat) }}>{s.pl_flat >= 0 ? "+" : ""}€{s.pl_flat?.toFixed(2)}</span>
                    </div>
                    <div className="slc-stats">
                      <span>{s.total} bets</span>
                      <span>{s.win_rate?.toFixed(1)}% win</span>
                      <span>x̄ {s.avg_odds?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {stats.weekly?.length > 0 && (
            <div className="stats-section">
              <h3 className="stats-section-title">{t("bets.weekly_evolution")}</h3>
              <BankrollChart weekly={stats.weekly} bets={[]} onBetClick={() => {}} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Baseball Tab ──────────────────────────────────────────────

function BaseballPickCard({ pick, franceOnly }) {
  const { t, i18n } = useTranslation();
  const relevant = (pick.value_bets || []).filter(v =>
    v.outcome === pick.predicted_winner && (!franceOnly || v.france_ok)
  );
  const bestVb  = relevant.sort((a, b) => b.odds - a.odds)[0];
  const potential = bestVb ? (pick.stake * bestVb.odds).toFixed(2) : null;
  return (
    <div className="pick-card">
      <div className="pick-header" style={{ borderLeft: "4px solid #1d4ed8" }}>
        <span className="pick-league">MLB</span>
        <span className="pick-date">{formatDate(pick.date, i18n.language)}</span>
      </div>
      <div className="pick-teams">
        <span className={`pick-team ${pick.predicted_winner === "home" ? "pick-team-predicted" : ""}`}>
          {pick.home_team}
        </span>
        <span className="pick-vs">vs</span>
        <span className={`pick-team ${pick.predicted_winner === "away" ? "pick-team-predicted" : ""}`}>
          {pick.away_team}
        </span>
      </div>
      <div className="pick-pred-row">
        <span className="pick-pred-label">{t("bets.pred_label")}:</span>
        <span className="pick-pred-val" style={{ color: "#22c55e" }}>{pick.predicted_team}</span>
        <span className="pick-conf">{t("bets.conf_label")} {(pick.confidence * 100).toFixed(0)}%</span>
      </div>
      {relevant.length > 0 && (
        <div className="pick-odds">
          <div className="pick-odds-title">Value bets</div>
          {relevant.slice(0, 3).map((vb, i) => (
            <div key={i} className="pick-odds-row">
              <span className="po-bm">{vb.bookmaker}</span>
              <span className="po-odds">{vb.odds.toFixed(2)}</span>
              <span className="po-edge" style={{ color: edgeColor(vb.edge) }}>+{vb.edge.toFixed(1)}%</span>
              <span className="po-kelly">Kelly {vb.kelly.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
      {potential && (
        <div className="pick-stake-row">
          <span className="pick-stake-label">{t("bets.stake_action", { amount: pick.stake })}</span>
          <span className="pick-stake-arrow">→</span>
          <span className="pick-stake-gain">€{potential} {t("bets.if_wins")}</span>
        </div>
      )}
    </div>
  );
}

function BaseballTab() {
  const [subtab, setSubtab]   = useState("picks");
  const [picks, setPicks]     = useState([]);
  const [doubles, setDoubles] = useState([]);
  const [triples, setTriples] = useState([]);
  const [history, setHistory] = useState([]);
  const [histTotal, setHistTotal] = useState(0);
  const [histPage, setHistPage]   = useState(0);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(false);
  const [franceOnly, setFranceOnly] = useState(true);
  const HIST_LIMIT = 50;

  const loadPicks = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/baseball/weekly-picks?days=14&min_confidence=0.6`);
      const data = await res.json();
      setPicks(data.picks || []);
      setDoubles(data.doubles || []);
      setTriples(data.triples || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/baseball/bets-history?limit=${HIST_LIMIT}&offset=${page * HIST_LIMIT}`);
      const data = await res.json();
      setHistory(data.bets || []);
      setHistTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/baseball/bets-stats`);
      setStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (subtab === "picks")   loadPicks();
    if (subtab === "history") loadHistory(histPage);
    if (subtab === "stats")   loadStats();
  }, [subtab, histPage]);

  const { t } = useTranslation();

  const SUBTABS = [
    { key: "picks",   label: "Picks" },
    { key: "history", label: t("bets.subtab_history") },
    { key: "stats",   label: t("bets.tennis_subtab_stats") },
  ];

  return (
    <div>
      <div className="bets-subtabs">
        {SUBTABS.map(s => (
          <button key={s.key} className={`bets-subtab ${subtab === s.key ? "active" : ""}`}
            onClick={() => setSubtab(s.key)}>{s.label}</button>
        ))}
        <button className={`france-toggle ${franceOnly ? "active" : ""}`}
          style={{ marginLeft: "auto" }} onClick={() => setFranceOnly(f => !f)}>
          {t("bets.france_only_short")}
        </button>
      </div>

      {loading && <div className="bets-loading">{t("common.loading")}</div>}

      {subtab === "picks" && !loading && (
        <div>
          {picks.length === 0 ? (
            <div className="bets-empty"><div className="bets-empty-icon">—</div><div>{t("bets.no_baseball_picks")}</div></div>
          ) : (
            <div>
              <div className="jornada-section">
                <div className="jsec-title">{t("bets.singles_baseball")}</div>
                <div className="picks-grid">
                  {picks.filter(p => p.best_edge >= 5).map((p, i) => (
                    <BaseballPickCard key={i} pick={p} franceOnly={franceOnly} />
                  ))}
                </div>
              </div>
              {doubles.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">{t("bets.doubles_section")}</div>
                  <div className="parlay-grid">
                    {doubles.slice(0, 3).map((d, i) => (
                      <SportParlayCard key={i} parlay={d} type="double" sport="baseball" />
                    ))}
                  </div>
                </div>
              )}
              {triples.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">{t("bets.triples_section")}</div>
                  <div className="parlay-grid">
                    {triples.map((tr, i) => (
                      <SportParlayCard key={i} parlay={tr} type="triple" sport="baseball" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {subtab === "history" && !loading && (
        <div>
          {history.length === 0 ? (
            <div className="bets-empty"><div className="bets-empty-icon">—</div><div>{t("bets.no_history_yet")}</div></div>
          ) : (
            <div className="hist-table-wrap">
              <div className="hist-meta">
                {t("bets.history_page_meta", { total: histTotal, page: histPage + 1, pages: Math.ceil(histTotal / HIST_LIMIT) })}
              </div>
              <table className="hist-table">
                <thead>
                  <tr>
                    <th>{t("bets.hist_col_date")}</th><th>{t("bets.hist_col_league")}</th><th>{t("bets.hist_col_match")}</th><th>{t("bets.col_pick")}</th>
                    <th>{t("bets.hist_col_bm")}</th><th>{t("bets.hist_col_odds")}</th><th>{t("bets.hist_col_edge")}</th><th>{t("bets.hist_col_res")}</th><th>{t("bets.hist_col_pl")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((b, i) => <BaseballHistoryRow key={i} bet={b} />)}
                </tbody>
              </table>
              <div className="hist-pagination">
                <button disabled={histPage === 0} onClick={() => setHistPage(p => p - 1)}>{t("bets.hist_prev")}</button>
                <button disabled={(histPage + 1) * HIST_LIMIT >= histTotal} onClick={() => setHistPage(p => p + 1)}>{t("bets.hist_next")}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {subtab === "stats" && !loading && stats?.global && (
        <div className="stats-section-wrap">
          <div className="stats-cards-grid">
            <StatCard label="P&L Total" value={`${stats.global.combined_pl >= 0 ? "+" : ""}€${stats.global.combined_pl?.toFixed(2)}`}
              sub={`ROI ${stats.global.roi_flat >= 0 ? "+" : ""}${stats.global.roi_flat?.toFixed(1)}%`} color={plColor(stats.global.combined_pl)} />
            <StatCard label="Win Rate" value={`${stats.global.win_rate?.toFixed(1)}%`}
              sub={`${stats.global.won_bets}/${stats.global.total_bets} bets`} color="#f59e0b" />
            <StatCard label="Avg Edge" value={`+${stats.global.avg_edge?.toFixed(1)}%`}
              sub={`${t("bets.stats_avg_odds_sub", { odds: stats.global.avg_odds?.toFixed(2) })}`} color="#3b82f6" />
            <StatCard label="Bankroll" value={`€${stats.global.combined_bankroll?.toFixed(0)}`}
              sub="Base €1000" color="#22c55e" />
          </div>
          {stats.by_outcome?.length > 0 && (
            <div className="stats-section">
              <h3 className="stats-section-title">{t("bets.home_vs_away")}</h3>
              <div className="stats-outcome-grid">
                {stats.by_outcome.map(o => (
                  <div key={o.outcome} className="stats-outcome-card"
                    style={{ borderTop: `3px solid ${o.outcome === "home" ? "#22c55e" : "#3b82f6"}` }}>
                    <div className="soc-label">{o.label}</div>
                    <div className="soc-pl" style={{ color: plColor(o.pl_flat) }}>
                      {o.pl_flat >= 0 ? "+" : ""}€{o.pl_flat?.toFixed(2)}
                    </div>
                    <div className="soc-stats">
                      <span>{o.total} bets</span>
                      <span>{o.win_rate?.toFixed(1)}% win</span>
                      <span>x̄ {o.avg_odds?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {stats.weekly?.length > 0 && (
            <div className="stats-section">
              <h3 className="stats-section-title">{t("bets.weekly_evolution")}</h3>
              <BankrollChart weekly={stats.weekly} bets={[]} onBetClick={() => {}} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────

export default function BetsPage({ league, season, isBaseball = false, isTennis = false }) {
  const { t, i18n } = useTranslation();
  // internalSportTab only applies when top bar is "All" or a football league
  const [internalSportTab, setInternalSportTab] = useState("football");
  // Derived: top-bar sport always wins
  const sportTab = isTennis ? "tennis" : isBaseball ? "baseball" : internalSportTab;
  const setSportTab = (s) => {
    if (!isTennis && !isBaseball) setInternalSportTab(s);
  };
  const [subtab, setSubtab] = useState("jornada");
  const [weeklyPicks, setWeeklyPicks] = useState([]);
  const [doubles, setDoubles] = useState([]);
  const [triples, setTriples] = useState([]);
  const [isFallback, setIsFallback] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [bookmakers, setBookmakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [franceOnly, setFranceOnly] = useState(true);
  const [histPage, setHistPage] = useState(0);
  const [histTotal, setHistTotal] = useState(0);
  const [parlays, setParlays] = useState([]);
  const [selectedBet, setSelectedBet] = useState(null);
  const [tennisBets, setTennisBets]     = useState([]);
  const [baseballBets, setBaseballBets] = useState([]);
  const HIST_LIMIT = 50;

  const fetchWeeklyPicks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ min_confidence: 0.6, days: 14 });
      if (league) params.append("league", league);
      const res = await fetch(`${API}/bets/weekly-picks?${params}`);
      const data = await res.json();
      setWeeklyPicks(data.picks || []);
      setDoubles(data.doubles || []);
      setTriples(data.triples || []);
      setIsFallback(data.is_fallback || false);
      setNeedsUpdate(data.needs_update || false);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [league]);

  const fetchHistory = useCallback(
    async (page = 0) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: HIST_LIMIT,
          offset: page * HIST_LIMIT,
        });
        if (league) params.append("league", league);
        if (season) params.append("season", season);

        const parlayParams = new URLSearchParams({ limit: 200 });
        if (league && league !== "all") parlayParams.append("league", league);
        if (season && season !== "all") parlayParams.append("season", season);

        const [histRes, parlayRes] = await Promise.all([
          fetch(`${API}/bets/history?${params}`),
          fetch(`${API}/bets/parlay-history?${parlayParams}`),
        ]);
        const histData   = await histRes.json();
        const parlayData = await parlayRes.json();
        setHistory(histData.bets || []);
        setHistTotal(histData.total || 0);
        setParlays(parlayData.parlays || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [league, season],
  );

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (league) params.append("league", league);
      if (season) params.append("season", season);

      const histParams = new URLSearchParams({ limit: 200, offset: 0 });
      if (league) histParams.append("league", league);
      if (season) histParams.append("season", season);

      const parlayParams = new URLSearchParams({ limit: 200 });
      if (league) parlayParams.append("league", league);
      if (season) parlayParams.append("season", season);

      const [statsRes, bmRes, histRes, parlayRes] = await Promise.all([
        fetch(`${API}/bets/stats?${params}`),
        fetch(`${API}/bets/bookmakers`),
        fetch(`${API}/bets/history?${histParams}`),
        fetch(`${API}/bets/parlay-history?${parlayParams}`),
      ]);
      setStats(await statsRes.json());
      setBookmakers(await bmRes.json());
      const histData    = await histRes.json();
      const parlayData  = await parlayRes.json();
      setHistory(histData.bets || []);
      setParlays(parlayData.parlays || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [league, season]);

  const fetchTennisBets = useCallback(async () => {
    if (league) return;
    try {
      const res  = await fetch(`${API}/tennis/value-bets`);
      const data = await res.json();
      setTennisBets(data.value_bets || []);
    } catch (e) { setTennisBets([]); }
  }, [league]);

  const fetchBaseballBets = useCallback(async () => {
    if (league) return;
    try {
      const res  = await fetch(`${API}/baseball/value-bets`);
      const data = await res.json();
      setBaseballBets(data || []);
    } catch (e) { setBaseballBets([]); }
  }, [league]);

  useEffect(() => {
    if (subtab === "picks" || subtab === "jornada") {
      fetchWeeklyPicks();
      fetchTennisBets();
      fetchBaseballBets();
    }
    if (subtab === "history") fetchHistory(histPage);
    if (subtab === "stats") fetchStats();
  }, [subtab, league, season, histPage]);

  const dayGroups = groupByDay(weeklyPicks);

  const SPORT_TABS = [
    { key: "football", label: t("bets.sport_football") },
    { key: "tennis",   label: t("bets.sport_tennis") },
    { key: "baseball", label: t("bets.sport_baseball") },
  ];

  const SUBTABS = [
    { key: "jornada",    label: t("bets.subtab_jornada") },
    { key: "picks",      label: t("bets.subtab_picks") },
    { key: "history",    label: t("bets.subtab_history") },
    { key: "stats",      label: t("bets.subtab_stats") },
  ];

  return (
    <div className="bets-page">
      {/* Sport tabs — solo visibles cuando el top bar no ha fijado un deporte */}
      {!isTennis && !isBaseball && (
        <div className="sport-tabs">
          {SPORT_TABS.map(tab => (
            <button
              key={tab.key}
              className={`sport-tab ${sportTab === tab.key ? "active" : ""}`}
              onClick={() => setSportTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Global balance bar */}
      <GlobalBalance onSportClick={(s) => setSportTab(s)} />

      {/* Tennis tab */}
      {sportTab === "tennis" && <TennisTab />}

      {/* Baseball tab */}
      {sportTab === "baseball" && <BaseballTab />}

      {/* Football tab */}
      {sportTab !== "tennis" && sportTab !== "baseball" && <>
      {/* Subtabs */}
      <div className="bets-subtabs">
        {SUBTABS.map((tab) => (
          <button
            key={tab.key}
            className={`bets-subtab ${subtab === tab.key ? "active" : ""}`}
            onClick={() => setSubtab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div className="bets-loading">{t("bets.loading")}</div>}

      {/* ── JORNADA ───────────────────────────────────────────── */}
      {subtab === "jornada" &&
        !loading &&
        (() => {
          const simpleBets = weeklyPicks.filter(
            (p) =>
              p.best_edge >= 10 &&
              p.value_bets.some(
                (v) => v.outcome === p.predicted_result && v.odds >= 2.0,
              ),
          );
          const topDouble = doubles[0] || null;
          const topTriple = triples[0] || null;
          const simpleStake = simpleBets.length * 10;
          const totalStake =
            simpleStake + (topDouble ? 10 : 0) + (topTriple ? 10 : 0);
          const pendingBaseball = baseballBets.filter(
            (b) => b.won === null || b.won === undefined,
          );

          if (!needsUpdate && !simpleBets.length && !topDouble && !topTriple && !tennisBets.length && !pendingBaseball.length)
            return (
              <div className="bets-empty">
                <div className="bets-empty-icon">—</div>
                <div>{t("bets.empty_jornada")}</div>
                <div className="bets-empty-sub">
                  {t("bets.empty_jornada_sub")}
                </div>
              </div>
            );

          if (needsUpdate && !tennisBets.length && !pendingBaseball.length)
            return (
              <div className="bets-empty">
                <div className="bets-empty-icon">—</div>
                <div>{t("bets.empty_jornada")}</div>
                <div className="bets-empty-sub">{t("bets.empty_jornada_sub")}</div>
              </div>
            );

          return (
            <div className="jornada-view">
              {!needsUpdate && (
                <>
                  <div className="jornada-header">
                    <div className="jh-title">{t("bets.jornada_title")}</div>
                    <div className="jh-meta">
                      {weeklyPicks.length > 0 &&
                        `${weeklyPicks[0]?.date?.slice(0, 10)} — ${weeklyPicks[weeklyPicks.length - 1]?.date?.slice(0, 10)}`}
                    </div>
                    <button
                      className={`france-toggle ${franceOnly ? "active" : ""}`}
                      onClick={() => setFranceOnly((f) => !f)}
                    >
                      {t("bets.fr_toggle")}
                    </button>
                  </div>

                  {simpleBets.length > 0 && (
                    <div className="jornada-section">
                      <div className="jsec-title">
                        {t("bets.simples_title")}{" "}
                        <span className="jsec-hint">{t("bets.simples_hint")}</span>
                      </div>
                      {simpleBets.map((p, i) => (
                        <BetInstruction
                          key={p.match_id}
                          pick={p}
                          franceOnly={franceOnly}
                          index={i + 1}
                        />
                      ))}
                    </div>
                  )}

                  {(topDouble || topTriple) && (
                    <div className="jornada-section">
                      <div className="jsec-title">
                        {t("bets.combinadas_title")}{" "}
                        <span className="jsec-hint">
                          {t("bets.combinadas_hint")}
                        </span>
                      </div>
                      <ParlaySlip parlay={topDouble} />
                      <ParlaySlip parlay={topTriple} type="triple" />
                    </div>
                  )}

                  <div className="jornada-summary">
                    <div className="jsum-title">{t("bets.summary_title")}</div>
                    <div className="jsum-rows">
                      {simpleBets.length > 0 && (
                        <div className="jsum-row">
                          <span>
                            {simpleBets.length === 1
                              ? t("bets.summary_single", { count: simpleBets.length })
                              : t("bets.summary_singles", { count: simpleBets.length })}
                          </span>
                          <span>€{simpleStake}</span>
                        </div>
                      )}
                      {topDouble && (
                        <div className="jsum-row">
                          <span>{t("bets.double_label")}</span>
                          <span>€10</span>
                        </div>
                      )}
                      {topTriple && (
                        <div className="jsum-row">
                          <span>{t("bets.triple_label")}</span>
                          <span>€10</span>
                        </div>
                      )}
                      <div className="jsum-row jsum-total">
                        <span>{t("bets.summary_total")}</span>
                        <span>€{totalStake}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {!league && tennisBets.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">
                    Tenis ATP{" "}
                    <span className="jsec-hint">value bets</span>
                  </div>
                  {tennisBets.map((bet, i) => (
                    <TennisBetCard key={i} bet={bet} />
                  ))}
                </div>
              )}

              {!league && pendingBaseball.length > 0 && (
                <div className="jornada-section">
                  <div className="jsec-title">
                    Baseball MLB{" "}
                    <span className="jsec-hint">value bets</span>
                  </div>
                  {pendingBaseball.map((bet, i) => (
                    <BaseballBetCard key={i} bet={bet} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

      {/* ── PICKS DE LA SEMANA ────────────────────────────────── */}
      {subtab === "picks" && !loading && (
        <div className="bets-picks">
          <StrategyBanner />

          {needsUpdate && (
            <div className="bets-needs-update">
              {t("bets.needs_update")}
            </div>
          )}

          {!needsUpdate && weeklyPicks.length === 0 && (
            <div className="bets-empty">
              <div className="bets-empty-icon">—</div>
              <div>{t("bets.empty_picks")}</div>
            </div>
          )}

          {weeklyPicks.length > 0 && (
            <>
              {isFallback && (
                <div className="picks-fallback-banner">
                  {t("bets.fallback_banner")}
                </div>
              )}

              <div className="picks-controls">
                <span className="picks-count">
                  {weeklyPicks.length} picks ·{" "}
                  {isFallback
                    ? t("bets.fallback_period")
                    : t("bets.upcoming_period")}
                </span>
                <button
                  className={`france-toggle ${franceOnly ? "active" : ""}`}
                  onClick={() => setFranceOnly((f) => !f)}
                >
                  {t("bets.fr_toggle_picks")}
                </button>
              </div>

              {(doubles.length > 0 || triples.length > 0) && (
                <div className="parlays-section">
                  <div className="parlays-section-title">
                    {t("bets.parlays_title")}
                    <span className="parlays-section-hint">
                      {t("bets.parlays_hint")}
                    </span>
                  </div>
                  {doubles.map((p, i) => (
                    <ParlayCard
                      key={`d${i}`}
                      parlay={p}
                      franceOnly={franceOnly}
                      index={i + 1}
                    />
                  ))}
                  {triples.map((p, i) => (
                    <ParlayCard
                      key={`t${i}`}
                      parlay={p}
                      franceOnly={franceOnly}
                      type="triple"
                      index={i + 1}
                    />
                  ))}
                </div>
              )}

              {dayGroups.map(([day, picks]) => (
                <div key={day} className="picks-day-group">
                  <div className="picks-day-label">
                    {formatDate(day, i18n.language)}
                  </div>
                  <div className="picks-grid">
                    {picks.map((pick) => (
                      <PickCard
                        key={pick.match_id}
                        pick={pick}
                        franceOnly={franceOnly}
                      />
                    ))}
                  </div>
                </div>
              ))}

            </>
          )}

          {!league && tennisBets.length > 0 && (
            <div className="picks-day-group">
              <div className="picks-day-label" style={{ color: '#22c55e' }}>
                Tenis ATP
              </div>
              <div className="picks-grid">
                {tennisBets.map((bet, i) => (
                  <TennisBetCard key={i} bet={bet} />
                ))}
              </div>
            </div>
          )}

          {!league && baseballBets.filter((b) => b.won === null || b.won === undefined).length > 0 && (
            <div className="picks-day-group">
              <div className="picks-day-label" style={{ color: '#1d4ed8' }}>
                Baseball MLB
              </div>
              <div className="picks-grid">
                {baseballBets
                  .filter((b) => b.won === null || b.won === undefined)
                  .map((bet, i) => (
                    <BaseballBetCard key={i} bet={bet} />
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY ───────────────────────────────────────────── */}
      {subtab === "history" && !loading && (
        <div className="bets-history">
          {history.length === 0 ? (
            <div className="bets-empty">
              <div className="bets-empty-icon">—</div>
              <div>{t("bets.hist_empty")}</div>
              <div className="bets-empty-sub">{t("bets.hist_empty_sub")}</div>
            </div>
          ) : (
            <>
              <div className="bets-section-header">
                <span className="bets-count">
                  {t("bets.hist_count", { count: histTotal })}
                </span>
                <span className="bets-hint">{t("bets.hist_stake_hint")}</span>
              </div>

              <ParlayHistorySection parlays={parlays} onBetClick={setSelectedBet} />

              <div className="hist-table-wrap">
                <table className="hist-table">
                  <thead>
                    <tr>
                      <th>{t("bets.hist_col_date")}</th>
                      <th>{t("bets.hist_col_league")}</th>
                      <th>{t("bets.hist_col_match")}</th>
                      <th>{t("bets.hist_col_result")}</th>
                      <th>{t("bets.hist_col_bm")}</th>
                      <th>{t("bets.hist_col_odds")}</th>
                      <th>{t("bets.hist_col_edge")}</th>
                      <th>{t("bets.hist_col_res")}</th>
                      <th>{t("bets.hist_col_pl")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((bet, i) => (
                      <HistoryRow key={i} bet={bet} onClick={() => setSelectedBet(bet)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="hist-pagination">
                <button
                  disabled={histPage === 0}
                  onClick={() => setHistPage((p) => p - 1)}
                  className="hist-page-btn"
                >
                  {t("bets.hist_prev")}
                </button>
                <span className="hist-page-info">
                  {histPage * HIST_LIMIT + 1}–
                  {Math.min((histPage + 1) * HIST_LIMIT, histTotal)} /{" "}
                  {histTotal}
                </span>
                <button
                  disabled={(histPage + 1) * HIST_LIMIT >= histTotal}
                  onClick={() => setHistPage((p) => p + 1)}
                  className="hist-page-btn"
                >
                  {t("bets.hist_next")}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STATS / BALANCE ───────────────────────────────────── */}
      {subtab === "stats" && !loading && stats && (
        <div className="bets-stats">
          {/* Bankroll total combinado */}
          <div className="stats-section stats-section--combined">
            <div className="stats-combined-bk">
              <div className="scb-label">{t("bets.combined_bankroll_title")}</div>
              <div
                className="scb-value"
                style={{ color: stats.global.combined_bankroll >= 1000 ? "#22c55e" : "#ef4444" }}
              >
                €{stats.global.combined_bankroll.toFixed(2)}
              </div>
              <div className="scb-breakdown">
                <span>{t("bets.simples_breakdown", { pl: `${stats.global.pl_flat >= 0 ? "+" : ""}€${stats.global.pl_flat.toFixed(2)}` })}</span>
                <span className="scb-sep">·</span>
                <span>{t("bets.combinadas_breakdown", { pl: `${stats.parlays.pl_flat >= 0 ? "+" : ""}€${stats.parlays.pl_flat.toFixed(2)}` })}</span>
              </div>
            </div>
          </div>

          <div className="stats-section">
            <h3 className="stats-section-title">{t("bets.global_stats_singles")}</h3>
            <div className="stats-cards">
              <StatCard
                label={t("bets.stats_bankroll")}
                value={`€${stats.global.bankroll.toFixed(2)}`}
                sub={t("bets.stats_bankroll_sub")}
                color={stats.global.bankroll >= 1000 ? "#22c55e" : "#ef4444"}
                big
              />
              <StatCard
                label={t("bets.stats_roi")}
                value={`${stats.global.roi_flat >= 0 ? "+" : ""}${stats.global.roi_flat.toFixed(1)}%`}
                sub={t("bets.stats_pl_sub", {
                  pl: `${stats.global.pl_flat >= 0 ? "+" : ""}€${stats.global.pl_flat.toFixed(2)}`,
                })}
                color={stats.global.roi_flat >= 0 ? "#22c55e" : "#ef4444"}
              />
              <StatCard
                label={t("bets.stats_winrate")}
                value={`${stats.global.win_rate.toFixed(1)}%`}
                sub={t("bets.stats_winrate_sub", {
                  won: stats.global.won_bets,
                  total: stats.global.total_bets,
                })}
                color="#f59e0b"
              />
              <StatCard
                label={t("bets.stats_avg_edge")}
                value={`+${stats.global.avg_edge.toFixed(1)}%`}
                sub={t("bets.stats_avg_odds_sub", {
                  odds: stats.global.avg_odds.toFixed(2),
                })}
                color="#3b82f6"
              />
            </div>
          </div>

          {/* Combinadas — mismo componente que en Historial */}
          {parlays.length > 0 && (
            <div className="stats-section">
              <ParlayHistorySection parlays={parlays} onBetClick={setSelectedBet} />
            </div>
          )}

          <div className="stats-section">
            <h3 className="stats-section-title">{t("bets.stats_chart")}</h3>
            {(() => {
              const resolvedParlays = parlays
                .filter(p => p.won !== null && p.won !== undefined && p.pl_flat !== null)
                .map(p => ({
                  date: p.detected_at?.slice(0, 10),
                  pl_flat: p.pl_flat,
                  won: p.won,
                  odds: p.combined_odds,
                  home_team: p.type === "triple" ? t("bets.triple_label") : t("bets.double_label"),
                  away_team: p.legs.slice(0, 2).map(l => l.home_team).join(" · "),
                  _isParlay: true,
                }));
              const combinedHistory = [
                ...history.filter(b => b.pl_flat !== null),
                ...resolvedParlays,
              ].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
              return (
                <BankrollChart
                  weekly={stats.weekly}
                  bets={combinedHistory}
                  onBetClick={(b) => !b._isParlay && setSelectedBet(b)}
                />
              );
            })()}
          </div>

          <div className="stats-section">
            <h3 className="stats-section-title">{t("bets.stats_by_league")}</h3>
            <div className="stats-league-grid">
              {stats.by_league.map((lg) => (
                <div
                  key={lg.league}
                  className="stats-league-card"
                  style={{
                    borderLeft: `4px solid ${LEAGUE_COLORS[lg.league] || "#334155"}`,
                  }}
                >
                  <div className="slc-header">
                    <span>
                      <Flag league={lg.league} size="lg" />{" "}
                      {lg.league.replace("_", " ")}
                    </span>
                    <span style={{ color: plColor(lg.pl_flat) }}>
                      {lg.pl_flat >= 0 ? "+" : ""}€{lg.pl_flat.toFixed(2)}
                    </span>
                  </div>
                  <div className="slc-stats">
                    <span>{t("bets.stats_bets_n", { count: lg.total })}</span>
                    <span>{lg.win_rate.toFixed(1)}% win</span>
                    <span>
                      +{lg.avg_edge.toFixed(1)}% {t("bets.edge_label")}
                    </span>
                  </div>
                  <div className="slc-bar">
                    <div
                      className="slc-bar-fill"
                      style={{
                        width: `${lg.win_rate}%`,
                        background: LEAGUE_COLORS[lg.league] || "#334155",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="stats-section">
            <h3 className="stats-section-title">
              {t("bets.stats_by_outcome")}
            </h3>
            <div className="stats-outcome-grid">
              {stats.by_outcome.map((o) => (
                <div
                  key={o.outcome}
                  className="stats-outcome-card"
                  style={{
                    borderTop: `3px solid ${OUTCOME_COLORS[o.outcome]}`,
                  }}
                >
                  <div className="soc-label">{o.label}</div>
                  <div className="soc-pl" style={{ color: plColor(o.pl_flat) }}>
                    {o.pl_flat >= 0 ? "+" : ""}€{o.pl_flat.toFixed(2)}
                  </div>
                  <div className="soc-stats">
                    <span>{t("bets.stats_bets_n", { count: o.total })}</span>
                    <span>{o.win_rate.toFixed(1)}% win</span>
                    <span>x̄ {o.avg_odds.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {bookmakers.length > 0 && (
            <div className="stats-section">
              <h3 className="stats-section-title">{t("bets.stats_by_bm")}</h3>
              <div className="bm-table-wrap">
                <table className="bm-table">
                  <thead>
                    <tr>
                      <th>{t("bets.bookmaker_label")}</th>
                      <th>{t("bets.bm_detected")}</th>
                      <th>{t("bets.bm_validated")}</th>
                      <th>{t("bets.bm_winrate")}</th>
                      <th>{t("bets.bm_avg_edge")}</th>
                      <th>{t("bets.hist_col_pl")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookmakers.slice(0, 20).map((bm) => (
                      <tr key={bm.bookmaker}>
                        <td className="bm-name">
                          {isFranceOk(bm.bookmaker) && (
                            <span className="bm-fr-flag">FR</span>
                          )}
                          {bm.bookmaker}
                        </td>
                        <td>{bm.total_detected}</td>
                        <td>{bm.total_validated}</td>
                        <td>
                          {bm.win_rate > 0 ? `${bm.win_rate.toFixed(1)}%` : "—"}
                        </td>
                        <td style={{ color: "#3b82f6" }}>
                          +{bm.avg_edge.toFixed(1)}%
                        </td>
                        <td style={{ color: plColor(bm.pl_flat) }}>
                          {bm.pl_flat >= 0 ? "+" : ""}€{bm.pl_flat.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {subtab === "stats" && !loading && !stats && (
        <div className="bets-empty">
          <div className="bets-empty-icon">—</div>
          <div>{t("bets.no_stats")}</div>
        </div>
      )}

      {selectedBet && (
        <BetDetailModal bet={selectedBet} onClose={() => setSelectedBet(null)} />
      )}
      </>}
    </div>
  );
}
